import React, { useState, useEffect } from 'react';
import { ChevronDown, AlertCircle, Loader2, Hash, Star } from 'lucide-react'; // Added Star icon here
import { useApp } from '../context/AppContext';
import Layout from './Layout';

const InputField = ({ label, icon: Icon, ...props }) => (
  <div className="space-y-1">
    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">
      {label}
    </label>
    <div className="bg-[#151525] border border-white/10 rounded-2xl flex items-center px-4 focus-within:border-purple-500 transition-all">
      {Icon && <Icon size={18} className="text-purple-500 mr-2" />}
      <input
        {...props}
        className="bg-transparent outline-none flex-1 py-4 text-base font-bold text-white w-full"
      />
    </div>
  </div>
);

const AdminDashboard = ({ onNav }) => {
  const { addSession } = useApp();
  const [form, setForm] = useState({ 
    room: '', 
    dancerId: '', 
    sessionType: '15',
    numDances: '1', 
    extraCharge: '' // Controlled here inside form state
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [apiTotal, setApiTotal] = useState(0);
  const [dancersList, setDancersList] = useState([]);

  // Helper function to format local time safely to MySQL DATETIME format (YYYY-MM-DD HH:MM:SS)
  const formatLocalToMySQL = (dateObj) => {
    const pad = (num) => String(num).padStart(2, '0');
    
    const year = dateObj.getFullYear();
    const month = pad(dateObj.getMonth() + 1);
    const day = pad(dateObj.getDate());
    const hours = pad(dateObj.getHours());
    const minutes = pad(dateObj.getMinutes());
    const seconds = pad(dateObj.getSeconds());
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('https://companylocker.com/admin/wp-json/dpt/v1/total-today');
        const data = await response.json();
        if (data.success) {
          setApiTotal(data.total_amount);
          if (data.subscribers) setDancersList(data.subscribers);
        }
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
      }
    };
    fetchData();
  }, []);
  
  
  const handleStart = async () => {
    if (!form.room || !form.dancerId) return setError('Missing Room or Dancer');
    
    setLoading(true);
    setError('');

    const selectedDancer = dancersList.find(d => d.id.toString() === form.dancerId.toString());
    const startTime = Date.now();
    const totalMinutes = parseInt(form.sessionType) * parseInt(form.numDances || 1);
    const durationMs = totalMinutes * 60000;

    try {
      const response = await fetch('https://companylocker.com/admin/wp-json/dpt/v1/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          room_name: form.room,
          user_id: form.dancerId,
          dancer_name: selectedDancer ? selectedDancer.name : 'Unknown',
          duration: form.sessionType,
          dance: form.numDances,
          extra_charge: form.extraCharge || '0', 
          start_time: formatLocalToMySQL(new Date(startTime)),
          end_time: formatLocalToMySQL(new Date(startTime + durationMs))
        }),
      });

      const result = await response.json();

      if (response.ok) {
        addSession({
          id: startTime,
          room: form.room,
          dancer: selectedDancer ? selectedDancer.name : 'Unknown',
          dancerId: form.dancerId,
          sessionType: totalMinutes, 
          dance: form.numDances,
          start: startTime,
          expectedEnd: startTime + durationMs,
          status: 'open',
        });
        setForm({ room: '', dancerId: '', sessionType: '15', numDances: '1', extraCharge: '' });
        onNav('history');
      } else {
        setError(result.message || 'Failed to start session');
      }
    } catch (err) {
      setError('Network error. Check server connection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout activeTab="dash" onNav={onNav}>
      <header className="py-8 space-y-4">
        <div className="w-full bg-gradient-to-br from-purple-900/40 to-[#0a0a1a] border border-white/10 rounded-[32px] p-6 text-center shadow-2xl backdrop-blur-xl">
          <p className="text-[10px] font-black text-purple-400 uppercase tracking-widest mb-1">
            Today's Club Net Earnings
          </p>
          <h2 className="text-4xl font-black tracking-tighter text-white">
            ${apiTotal.toLocaleString()}
          </h2>
        </div>
      </header>

      <div className="space-y-6 bg-[#0a0a1a] p-6 rounded-[32px] border border-white/5 shadow-2xl">
        {error && (
          <div className="text-red-500 text-[10px] font-black uppercase flex items-center gap-2 bg-red-500/10 p-3 rounded-xl border border-red-500/20">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        {/* Room Input */}
        <div>
          <label className="text-[10px] font-black text-gray-400 uppercase mb-2 block tracking-widest px-1">
            Room
          </label>
          <input
            className="w-full bg-[#151525] border border-white/10 rounded-2xl px-5 py-4 outline-none text-base font-bold text-white focus:border-purple-500 transition-all"
            placeholder="Room Name"
            value={form.room}
            onChange={(e) => setForm({ ...form, room: e.target.value })}
            disabled={loading}
          />
        </div>

        {/* Dancer Selection */}
        <div>
          <label className="text-[10px] font-black text-gray-400 uppercase mb-2 block tracking-widest px-1">
            Dancer
          </label>
          <div className="relative">
            <select
              className="w-full bg-[#151525] border border-white/10 rounded-2xl px-5 py-4 outline-none text-base font-bold text-white appearance-none"
              value={form.dancerId}
              onChange={(e) => setForm({ ...form, dancerId: e.target.value })}
              disabled={loading}
            >
              <option value="" hidden>
                {dancersList.length > 0 ? 'Select Dancer...' : 'Loading dancers...'}
              </option>
              {dancersList.map((dancer) => (
                <option key={dancer.id} value={dancer.id} className="bg-[#0a0a1a]">
                  {dancer.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 text-purple-500 pointer-events-none" size={18} />
          </div>
        </div>

        {/* Duration Grid */}
        <div>
          <label className="text-[10px] font-black text-gray-400 uppercase mb-2 block tracking-widest px-1">
            Duration (Single Dance)
          </label>
          <div className="grid grid-cols-3 gap-2">
            {['4', '15', '30'].map((time) => (
              <button
                key={time}
                disabled={loading}
                onClick={() => setForm({ ...form, sessionType: time })}
                className={`py-4 rounded-xl text-xs font-black border transition-all ${
                  form.sessionType === time
                    ? 'bg-white text-black border-white'
                    : 'bg-[#151525] border-white/5 text-gray-400'
                }`}
              >
                {time} MIN
              </button>
            ))}
          </div>
        </div>

        {/* Total Dances Input */}
        <InputField
          label="Total Dances"
          icon={Hash}
          type="number"
          inputMode="numeric"
          pattern="[0-9]*"
          min="1"
          value={form.numDances}
          onKeyDown={(e) => {
            if (['.', '-', 'e', '+'].includes(e.key)) {
              e.preventDefault();
            }
          }}
          onChange={(e) => {
            const val = e.target.value;
            if (val === '' || (Number(val) >= 1 && !val.includes('.'))) {
              setForm({ ...form, numDances: val });
            }
          }}
          onBlur={() => {
            if (!form.numDances || Number(form.numDances) < 1) {
              setForm({ ...form, numDances: '1' });
            }
          }}
          disabled={loading}
        />

        {/* Fixed Extra Charge Field to utilize form and setForm */}
        <InputField
          label="Entertainer Extra Charge ($)"
          icon={Star}
          type="number"
          inputMode="decimal"
          placeholder="0.00"
          value={form.extraCharge}
          onChange={(e) => setForm({ ...form, extraCharge: e.target.value })}
          disabled={loading}
        />

        <button
          onClick={handleStart}
          disabled={loading}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 py-5 rounded-2xl font-black text-xs tracking-widest mt-6 shadow-lg shadow-purple-500/20 active:scale-95 transition-all flex justify-center items-center text-white"
        >
          {loading ? <Loader2 className="animate-spin" size={18} /> : 'START SESSION'}
        </button>
      </div>
    </Layout>
  );
};

export default AdminDashboard;